/**
 * arbi files list                       — list ephemeral files
 * arbi files upload <path>              — upload a file
 * arbi files get <id>                   — show a file's metadata
 * arbi files delete <id>                — delete a file
 * arbi files content <id> [-o <path>]   — write the file's bytes to disk (or stdout)
 *
 * OpenAI-compatible Files API surface. These files are independent of the
 * workspace document index — they're meant for the Responses API
 * (`arbi ask -b`) where an agent attaches a short-lived file to a single
 * request. Until this command existed, the entire file subsystem was
 * unreachable from the CLI even though the SDK has full coverage.
 */

import { Command } from 'commander'
import { readFileSync } from 'node:fs'
import { writeFileSync } from 'node:fs'
import { basename, resolve } from 'node:path'
import { files as filesOps } from '@arbidocs/sdk'
import {
  runAction,
  resolveWorkspace,
  printTable,
  printJson,
  suggestSubcommandAndExit,
  emitListOrTable,
  truncate,
  dryRun,
} from '../helpers.js'
import { success, ref, error } from '../output.js'

function formatBytes(n: number): string {
  if (n < 1024) return `${n}B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}K`
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)}M`
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)}G`
}

export function registerFilesCommand(program: Command): void {
  const files = program
    .command('files')
    .description('Ephemeral files: list, upload, get, delete, fetch content')

  files
    .command('list')
    .description('List ephemeral files attached to your account')
    .option('--purpose <name>', 'Filter by purpose (default: assistants)')
    .option('-l, --limit <n>', 'Cap to N entries', (v) => parseInt(v, 10))
    .option('--json', 'Output as JSON')
    .action((opts: { purpose?: string; limit?: number; json?: boolean }) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace()
        const data = await filesOps.listFiles(arbi, {
          purpose: opts.purpose ?? null,
          limit: opts.limit,
        })
        // The OpenAI envelope is `{ data: [...], object: 'list' }` — surface
        // the array directly for human callers, but let `--json` consumers
        // see whatever shape the SDK returned (some downstream tools want
        // the `object: 'list'` discriminator).
        const items = (data.data ?? []) as Array<Record<string, unknown>>

        if (opts.json) {
          printJson(data)
          return
        }
        if (!emitListOrTable(items, opts, 'No files found.')) return

        printTable(
          [
            { header: 'ID', width: 30, value: (r) => r.id as string },
            {
              header: 'NAME',
              width: 30,
              value: (r) => truncate((r.filename as string) ?? '', 29),
            },
            { header: 'BYTES', width: 10, value: (r) => formatBytes((r.bytes as number) ?? 0) },
            { header: 'PURPOSE', width: 14, value: (r) => (r.purpose as string) ?? '' },
          ],
          items
        )
      })()
    )

  files
    .command('upload <path>')
    .description('Upload a file (file ID is returned for use with arbi ask -b --attach)')
    .option('--purpose <name>', 'OpenAI purpose tag (default: assistants)', 'assistants')
    .action((path: string, opts: { purpose?: string }) =>
      runAction(async () => {
        const { loginResult, config } = await resolveWorkspace()
        const filePath = resolve(path)
        let bytes: Buffer
        try {
          bytes = readFileSync(filePath)
        } catch {
          error(`Cannot read file: ${filePath}`)
          process.exit(3)
        }
        const blob = new Blob([new Uint8Array(bytes)])
        const data = await filesOps.uploadFile(
          {
            baseUrl: config.baseUrl,
            accessToken: loginResult.accessToken,
          } as Parameters<typeof filesOps.uploadFile>[0],
          blob,
          basename(filePath),
          opts.purpose ?? 'assistants'
        )
        // Agents extract the file ID with: grep -oE 'file-[A-Za-z0-9]+'
        success(`Uploaded: ${ref(data.id)} (${formatBytes(data.bytes ?? 0)})`)
      })()
    )

  files
    .command('get <id>')
    .description("Show a file's metadata (always JSON — the metadata IS the data)")
    .action((id: string) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace()
        const data = await filesOps.getFile(arbi, id)
        printJson(data)
      })()
    )

  files
    .command('delete <id>')
    .description('Delete an ephemeral file')
    .option('--dry-run', 'Preview which file would be deleted (no SDK call)')
    .action((id: string, opts: { dryRun?: boolean }) =>
      runAction(async () => {
        if (opts?.dryRun) {
          dryRun('delete file', id)
          return
        }
        const { arbi } = await resolveWorkspace()
        await filesOps.deleteFile(arbi, id)
        success(`Deleted: ${ref(id)}`)
      })()
    )

  files
    .command('content <id>')
    .description("Fetch a file's raw bytes (writes to disk or stdout)")
    .option('-o, --output <path>', 'Output file path (use "-" for stdout)')
    .action((id: string, opts: { output?: string }) =>
      runAction(async () => {
        const { loginResult, config } = await resolveWorkspace()
        const res = await filesOps.getFileContent(
          {
            baseUrl: config.baseUrl,
            accessToken: loginResult.accessToken,
          } as Parameters<typeof filesOps.getFileContent>[0],
          id
        )
        const buf = Buffer.from(await res.arrayBuffer())
        if (!opts.output || opts.output === '-') {
          process.stdout.write(buf)
          return
        }
        writeFileSync(resolve(opts.output), buf)
        success(`Wrote ${formatBytes(buf.length)} → ${opts.output}`)
      })()
    )

  // Default action: list, with typo suggestion for unknown subcommands.
  files
    .allowUnknownOption(true)
    .allowExcessArguments(true)
    .action(async () => {
      const tail = files.args ?? []
      const subcommands = files.commands.map((c) => c.name())
      const firstNonFlag = tail.find((a) => !a.startsWith('-'))
      if (firstNonFlag && !subcommands.includes(firstNonFlag)) {
        suggestSubcommandAndExit('files', firstNonFlag, subcommands)
      }
      await files.commands.find((c) => c.name() === 'list')!.parseAsync(tail, { from: 'user' })
    })
}
